import { onCleanup, untrack } from '@zyra/core';
import { h, registerExit, registerLayout, type Child, type Props, type HTMLAttributes } from '@zyra/dom';
export interface MotionValues { opacity?: number; x?: number; y?: number; scale?: number; rotate?: number }
export interface MotionOptions { duration?: number; delay?: number; easing?: string }
export function reducedMotion(): boolean { return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false; }
function frame(values: MotionValues): Keyframe {
  const result: Keyframe = {};
  if (values.opacity !== undefined) result.opacity = values.opacity;
  if (['x', 'y', 'scale', 'rotate'].some(key => key in values)) {
    result.transform = `translate(${values.x ?? 0}px, ${values.y ?? 0}px) rotate(${values.rotate ?? 0}deg) scale(${values.scale ?? 1})`;
  }
  return result;
}
export function animate(element: HTMLElement, from: MotionValues, to: MotionValues, options: MotionOptions = {}) {
  const final = frame(to);
  if (!element.animate || reducedMotion()) {
    Object.assign(element.style, final);
    return { finished: Promise.resolve(), cancel() {}, reverse() {} };
  }
  const animation = element.animate([frame(from), final], {
    duration: options.duration ?? 280, delay: options.delay ?? 0, easing: options.easing ?? 'cubic-bezier(.2,.8,.2,1)', fill: 'both'
  });
  const finished = animation.finished.then(() => {
    Object.assign(element.style, animation.playbackRate < 0 ? frame(from) : final); animation.cancel();
  }, () => {});
  return { finished, cancel: () => animation.cancel(), reverse: () => animation.reverse() };
}
/** Damped physical spring. Returns cancellation and settles at the exact target. */
export function spring(from: number, to: number, update: (value: number) => void, options: {
  stiffness?: number; damping?: number; mass?: number; velocity?: number;
} = {}) {
  const stiffness = options.stiffness ?? 170, damping = options.damping ?? 26, mass = options.mass ?? 1;
  if (stiffness <= 0 || damping <= 0 || mass <= 0) throw new Error('Spring parameters must be positive');
  let position = from, velocity = options.velocity ?? 0, last = performance.now(), id = 0, canceled = false;
  let resolve!: () => void;
  const finished = new Promise<void>(done => { resolve = done; });
  const cancel = () => { canceled = true; cancelAnimationFrame(id); resolve(); };
  if (reducedMotion()) { update(to); resolve(); return { finished, cancel }; }
  const tick = (now: number) => {
    if (canceled) return;
    const elapsed = Math.min((now - last) / 1000, .064); last = now;
    const steps = Math.max(1, Math.ceil(elapsed / .008)), dt = elapsed / steps;
    for (let i = 0; i < steps; i++) {
      velocity += (-stiffness * (position - to) - damping * velocity) / mass * dt;
      position += velocity * dt;
    }
    update(position);
    if (Math.abs(velocity) < .01 && Math.abs(to - position) < .01) { update(to); resolve(); }
    else id = requestAnimationFrame(tick);
  };
  update(from); id = requestAnimationFrame(tick);
  return { finished, cancel };
}
export function timeline(steps: (() => { finished: Promise<unknown>; cancel(): void })[]) {
  let canceled = false, active: ReturnType<(typeof steps)[number]> | undefined;
  return {
    finished: (async () => { for (const step of steps) { if (canceled) break; active = step(); await active.finished; } })(),
    cancel() { canceled = true; active?.cancel(); }
  };
}
export async function presence(element: HTMLElement, exit: MotionValues, options?: MotionOptions): Promise<void> {
  const style = getComputedStyle(element);
  await animate(element, { opacity: Number(style.opacity) }, exit, options).finished;
  element.remove();
}
export interface MotionFields { enter?: MotionValues; exit?: MotionValues; layout?: boolean; hover?: MotionValues; press?: MotionValues; focus?: MotionValues; transition?: MotionOptions }
type MotionProps = Props & MotionFields;
export type MotionComponents = { [Tag in keyof HTMLElementTagNameMap]: (props: HTMLAttributes<Tag> & MotionFields) => Child };
export const Motion = new Proxy({} as MotionComponents, {
  get: (_, tag: string) => (props: MotionProps) => {
    const filtered = Object.create(null) as Props;
    const reserved = new Set(['enter', 'hover', 'press', 'focus', 'transition', 'layout', 'exit']);
    for (const key of Object.keys(props)) if (!reserved.has(key)) Object.defineProperty(filtered, key, { enumerable: true, get: () => props[key] });
    const element = h(tag, filtered) as HTMLElement;
    const base: MotionValues = { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 };
    let current = base, running: ReturnType<typeof animate> | undefined;
    const play = (next: MotionValues) => {
      running?.cancel(); running = animate(element, current, next, props.transition); current = next;
    };
    if (props.enter) { current = { ...base, ...props.enter }; play(base); }
    if (props.exit) registerExit(element, () => animate(element, current, { ...current, ...props.exit }, props.transition).finished);
    let layoutAnimation: Animation | undefined;
    if (props.layout) registerLayout(element, previous => {
      layoutAnimation?.cancel();
      if (reducedMotion() || !element.animate) return;
      const next = element.getBoundingClientRect();
      if (!next.width || !next.height || !previous.width || !previous.height) return;
      const x = previous.left - next.left, y = previous.top - next.top;
      if (Math.abs(x) < .5 && Math.abs(y) < .5 && Math.abs(previous.width - next.width) < .5 && Math.abs(previous.height - next.height) < .5) return;
      layoutAnimation = element.animate([
        { translate: `${x}px ${y}px`, scale: `${previous.width / next.width} ${previous.height / next.height}`, transformOrigin: 'top left' },
        { translate: '0px 0px', scale: '1 1', transformOrigin: 'top left' }
      ], { duration: props.transition?.duration ?? 280, easing: props.transition?.easing ?? 'cubic-bezier(.2,.8,.2,1)' });
      void layoutAnimation.finished.catch(() => {});
    });
    let hover = false, press = false, focus = false;
    const refresh = () => play({ ...base, ...(hover ? props.hover : {}), ...(focus ? props.focus : {}), ...(press ? props.press : {}) });
    const listeners: [string, () => void][] = [
      ['pointerenter', () => { hover = true; refresh(); }], ['pointerleave', () => { hover = press = false; refresh(); }],
      ['pointerdown', () => { press = true; refresh(); }], ['pointerup', () => { press = false; refresh(); }],
      ['pointercancel', () => { press = false; refresh(); }], ['focus', () => { focus = true; refresh(); }], ['blur', () => { focus = false; refresh(); }]
    ];
    for (const [event, listener] of listeners) element.addEventListener(event, listener);
    const keyDown = (event: KeyboardEvent) => { if (event.key === 'Enter' || event.key === ' ') { press = true; refresh(); } };
    const keyUp = (event: KeyboardEvent) => { if (event.key === 'Enter' || event.key === ' ') { press = false; refresh(); } };
    element.addEventListener('keydown', keyDown); element.addEventListener('keyup', keyUp);
    onCleanup(() => { running?.cancel(); layoutAnimation?.cancel(); element.removeEventListener('keydown', keyDown); element.removeEventListener('keyup', keyUp); for (const [event, listener] of listeners) element.removeEventListener(event, listener); });
    return element;
  }
});
